import React, { useState, useMemo, useImperativeHandle, forwardRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid, TextField, Typography, Tabs, Tab, InputAdornment } from "@material-ui/core";
import { AutocompleteComponent } from "components/Autocomplete/AutocompleteComponent";
import { useForm } from "hooks/useForm";
import { createSpecialUnit } from "redux/actions/specialUnits/specialUnitsActions";
import { newDate } from "helpers/dateTools";
import { formatNumber } from "helpers/getNumberFormats";
import { orderArrayV2 } from "helpers/orderArray";
import { calculateSpecialUnitFields, ALL_NUMERIC_KEYS } from "./calculate";
import { INGRESOS_FIELDS, EGRESOS_FIELDS } from "./labels";

const formatInitialNumber = (val) => {
  if (val === "" || val === null || val === undefined) return "";
  const num = parseFloat(val);
  if (isNaN(num)) return "";
  return num.toLocaleString("en-US");
};

const buildInitialState = (data) => {
  const base = {
    unit: data?.unit || null,
    unitType: data?.unitType || "",
    date: data?.date ? data.date : newDate(),
    price: "",
    sale: "",
    costSale: "",
    imss: "",
    selfConsumption: "",
    perceptions: "",
    cylinderRepair: "",
    portableEquipmentMaintenance: "",
    tankAcquisition: "",
    tankAccessories: "",
    insuranceFinanceFees: "",
    commissions: "",
    efParticipation: "",
    specialPricePromotion: "",
    christmasBonus: "",
    taxes: "",
    claimsTheft: "",
    platformVoucher: "",
    foodVouchersAdvertising: "",
    floorRights: "",
    profit: "",
    adminExpenses: "",
    profitSharing1: "",
    profitSharing2: "",
  };
  if (data) {
    ALL_NUMERIC_KEYS.forEach((k) => {
      const formatted = formatInitialNumber(data[k]);
      base[k] = formatted;
      base[`${k}_`] = formatted;
    });
  }
  return base;
};

const FIELD_VALIDATIONS = {
  unit: { required: true },
  price: { required: true },
  date: { required: true },
  sale: { required: true },
  costSale: { required: true },
};

const SpecialUnitForm = forwardRef(({ editData, station, onClose }, ref) => {
  const dispatch = useDispatch();
  const { unitsMap } = useSelector(({ unitsReducer }) => unitsReducer);
  const [tab, setTab] = useState(0);

  const isEdit = !!editData;

  const { values, errors, onChange, onChangeNumberV2, setValues, validate } = useForm(buildInitialState(editData), FIELD_VALIDATIONS);

  const filteredUnits = useMemo(() => {
    if (!station?.id) return [];
    const mUnis = unitsMap
      .filter((u) => u.idStation === station.id && u.type.includes("MAYORISTA"))
      .map((u) => ({ ...u, label: u.number || u.name || u.id, value: u.id }));
    return orderArrayV2(mUnis, "label");
  }, [unitsMap, station]);

  const handleSave = () => {
    validate([], () => {
      const body = {
        idUnit: values.unit?.id || values.unit?.value || editData?.idUnit || "",
        unitType: values.unit?.type || editData?.unitType || "",
        idStation: station?.id,
        date: values.date,
        unit: values.unit?.label || values.unit?.number || values.unit?.name || "",
      };
      ALL_NUMERIC_KEYS.forEach((k) => {
        const num = parseFloat(String(values[k]).replace(/,/g, ""));
        body[k] = isNaN(num) ? 0 : num;
      });

      // Cálculos según documento
      const calculated = calculateSpecialUnitFields(body);
      Object.assign(body, calculated);

      dispatch(createSpecialUnit(body, onClose));
    });
  };

  useImperativeHandle(ref, () => ({ handleSave }));

  return (
    <>
      <Grid container spacing={2}>
        {isEdit ? (
          <>
            <Grid item xs={12} md={4}>
              <Typography variant="body1" color="primary">Unidad</Typography>
              <Typography variant="body1">{editData?.number || editData?.unit || editData?.idUnit}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body1" color="primary">Precio</Typography>
              <Typography variant="body1">$ {formatNumber(editData?.price)}</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body1" color="primary">Fecha</Typography>
              <Typography variant="body1">{values.date}</Typography>
            </Grid>
          </>
        ) : (
          <>
            <Grid item xs={12} md={4}>
              <AutocompleteComponent
                options={filteredUnits}
                name="unit"
                value={values.unit}
                label="Unidad"
                handleValues={setValues}
                textFieldProps={{
                  error: !!errors.unit,
                  helperText: errors.unit,
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Precio"
                name="price"
                value={values.price_ || values.price}
                onChange={(e) => onChangeNumberV2(e)}
                fullWidth
                variant="outlined"
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                inputProps={{ style: { textAlign: "right" } }}
                error={!!errors.price}
                helperText={errors.price}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Fecha"
                name="date"
                value={values.date}
                onChange={onChange}
                fullWidth
                variant="outlined"
                type="date"
                InputLabelProps={{ shrink: true }}
                error={!!errors.date}
                helperText={errors.date}
              />
            </Grid>
          </>
        )}
      </Grid>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        style={{ marginTop: 16 }}
        indicatorColor="primary"
        textColor="primary"
        variant="fullWidth"
      >
        <Tab label="Ingresos" />
        <Tab label="Egresos" />
      </Tabs>

      <Grid container spacing={2} style={{ marginTop: 8 }}>
        {tab === 0 &&
          INGRESOS_FIELDS.map(({ key, label }) => (
            <Grid item xs={12} md={4} key={key}>
              <Typography variant="body1" color="primary">{label}</Typography>
              {isEdit ? (
                <Typography variant="body1">$ {formatNumber(editData?.[key])}</Typography>
              ) : (
                <TextField
                  name={key}
                  value={values[`${key}_`] || values[key]}
                  onChange={(e) => onChangeNumberV2(e)}
                  fullWidth
                  variant="outlined"
                  size="small"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  inputProps={{ style: { textAlign: "right" } }}
                  error={!!errors[key]}
                  helperText={errors[key]}
                />
              )}
            </Grid>
          ))}
        {tab === 1 &&
          EGRESOS_FIELDS.map(({ key, label }) => (
            <Grid item xs={12} md={4} key={key}>
              <Typography variant="body1" color="primary">{label}</Typography>
              {isEdit ? (
                <Typography variant="body1">$ {formatNumber(editData?.[key])}</Typography>
              ) : (
                <TextField
                  name={key}
                  value={values[`${key}_`] || values[key]}
                  onChange={(e) => onChangeNumberV2(e)}
                  fullWidth
                  variant="outlined"
                  size="small"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  inputProps={{ style: { textAlign: "right" } }}
                />
              )}
            </Grid>
          ))}
      </Grid>
    </>
  );
});

export default SpecialUnitForm;
